/**
 * How long the Track step may spend building its suggestions before it stops
 * spinning and says so. The step runs a relaxation ladder of people searches, so
 * its cost is several upstream calls rather than one.
 */
export const TRACK_LOAD_TIMEOUT_MS = 30000;

/**
 * Rejects when `work` outlives its budget.
 *
 * A step that waits forever is the failure mode this exists to prevent: the Track
 * table renders "Loading matches…" for as long as the request is outstanding, and
 * an upstream that never answers leaves the user on that skeleton with no way
 * forward and nothing said. A budget turns that into a stated failure they can
 * retry or walk past.
 */
export const withBudget = async <T>(work: Promise<T>, budgetMs: number): Promise<T> => {
	let timer: ReturnType<typeof setTimeout> | null = null;

	const budget = new Promise<never>((_, reject) => {
		timer = setTimeout(
			() => reject(new Error("Track suggestions exceeded their budget")),
			budgetMs
		);
	});

	try {
		return await Promise.race([work, budget]);
	} finally {
		if (timer) {
			clearTimeout(timer);
		}
	}
};
